import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikValues } from 'formik';

import {
  IUseSetGroupEditTable,
  SetGroupEditTableMode,
} from '../../../components/reusable/set-group-edit-table/interfaces';
import { useDefaultSetItemRestSec } from '../../../hooks/use-default-set-item-rest-sec';
import { useExerciseResultsForSetAutofill } from '../../../hooks/use-exercise-results-for-set-autofill';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { ExerciseOptionType } from '../../../shared/constants';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { orderBy, replace } from '../../../utils/collections/array-utils';
import {
  changeSetNumber,
  getEmptyProgramSet,
  orderSets,
} from '../../../utils/training/set-utils';

function useEditTable(
  formik: FormikValues,
  programItemIdFromRoute?: string,
): IUseSetGroupEditTable<IProgramSet> {
  const { t } = useTranslation();
  const { showSaveSetButton, showUploadSetButton } = useUserSetPreferences();
  const { defaultRestForNewEmptyItem } = useDefaultSetItemRestSec();
  const exerciseResults = useExerciseResultsForSetAutofill(undefined);
  const onChangeSet = useCallback(
    (prevSets: IProgramSet[], set: IProgramSet) => {
      return replace(prevSets, set, ['setNumber', 'orderNumber']);
    },
    [],
  );

  const onRemoveSetsBySetNumber = useCallback(
    (prevSets: IProgramSet[], setNumber: number, orderNumber?: number) => {
      let currentSets = orderBy<IProgramSet>(
        [...prevSets],
        ['setNumber', 'orderNumber'],
      );

      const setsWithSameSetNumberCount = currentSets.filter(
        (set) => set.setNumber === setNumber,
      )?.length;

      if (orderNumber !== undefined && orderNumber !== null) {
        currentSets = currentSets.filter(
          (set) =>
            set.setNumber !== setNumber || set.orderNumber !== orderNumber,
        );
      } else {
        currentSets = currentSets.filter((set) => set.setNumber !== setNumber);
      }

      currentSets = currentSets
        .map((set) => {
          if (set.setNumber === setNumber && set.orderNumber > orderNumber) {
            return { ...set, orderNumber: set.orderNumber - 1 };
          }

          if (
            set.setNumber > setNumber &&
            (orderNumber === undefined ||
              orderNumber === null ||
              setsWithSameSetNumberCount === 1)
          ) {
            return { ...set, setNumber: set.setNumber - 1 };
          }

          return set;
        })
        .filter(Boolean);

      return currentSets;
    },
    [],
  );
  const onAddSet = useCallback(
    (prevSets: IProgramSet[], newSet?: IProgramSet) => {
      let currentSets = [...prevSets];

      if (newSet) {
        currentSets = [...currentSets, newSet];
      } else {
        currentSets = [
          ...currentSets,
          {
            ...getEmptyProgramSet(
              Math.max(...currentSets.map((set) => set.setNumber)) + 1,
              0,
              {
                haveOwner: false,
                programItemId:
                  programItemIdFromRoute ?? formik.values?.id ?? undefined,
                defaultItemRestSec: defaultRestForNewEmptyItem,
              },
            ),
          },
        ];
      }

      currentSets = orderSets(currentSets);

      return currentSets;
    },
    [defaultRestForNewEmptyItem, programItemIdFromRoute, formik.values?.id],
  );
  const onChangeSetOrder = useCallback(
    (prevSets: IProgramSet[], setNumber: number, direction: 'up' | 'down') => {
      let currentSets = [...prevSets];

      currentSets = changeSetNumber(currentSets, setNumber, direction);

      return currentSets;
    },
    [],
  );

  const onChangeSetWrapper = useCallback(
    (set: IProgramSet) => {
      formik.setValues((prev) => ({
        ...prev,
        programSets: onChangeSet(prev.programSets, set),
      }));
    },
    [onChangeSet],
  );

  const onRemoveSetsBySetNumberWrapper = useCallback(
    (setNumber: number, orderNumber?: number) => {
      formik.setValues((prev) => ({
        ...prev,
        programSets: onRemoveSetsBySetNumber(
          prev.programSets,
          setNumber,
          orderNumber,
        ),
      }));
    },
    [onRemoveSetsBySetNumber],
  );

  const onAddSetWrapper = useCallback(
    (newSet?: IProgramSet) => {
      formik.setValues((prev) => ({
        ...prev,
        programSets: onAddSet(prev.programSets, newSet),
      }));
    },
    [onAddSet],
  );

  const onChangeSetOrderWrapper = useCallback(
    (setNumber: number, direction: 'up' | 'down') => {
      formik.setValues((prev) => ({
        ...prev,
        programSets: onChangeSetOrder(prev.programSets, setNumber, direction),
      }));
    },
    [onChangeSetOrder],
  );

  return {
    options: {
      hideSuperset: false,
      hideSetsCount: false,
      hideExpand: false,
      hideHeader: false,
      hideSavedSetUploading: !showUploadSetButton,
      removeSetMode: 'both',
      hideSaveSet: !showSaveSetButton,
    },
    exerciseResults,
    mode: SetGroupEditTableMode.program,
    maxSetNumber: Math.max(
      ...(formik.values.programSets?.map((set) => set.setNumber) || [0]),
    ),
    submitCalled: formik.submitCount > 0,
    optionValidators: [
      {
        message: t('formikValidation.minValue').replace('{0}', '0'),
        validator: (value: number) => {
          return value && value < 0;
        },
      },
      {
        type: ExerciseOptionType.integer,
        message: t('formikValidation.integerValueRequired'),
        validator: (value: number) => {
          return +value === value && !Number.isInteger(value);
        },
      },
    ],
    onChangeSet: onChangeSetWrapper,
    onRemoveSetsBySetNumber: onRemoveSetsBySetNumberWrapper,
    onAddSet: onAddSetWrapper,
    onChangeSetOrder: onChangeSetOrderWrapper,
  };
}

export default useEditTable;
